interface Payload {
	hostname: string
	language: string
	referrer: string
	screen: string
	title: string
	url: string
	website: string
}

interface Umami {
	track: (
		data?: Payload | ((props: Payload) => Payload) | string,
		props?: Record<string, any>,
	) => void
	identify: (userId: string, data: Record<string, any>) => void
}

interface UmamiPluginConfig {
	src: string
	websiteId: string
	hostUrl?: string
	autoTrack?: boolean
	domains?: string[]
	tag?: string
	excludeSearch?: boolean
	excludeHash?: boolean
	doNotTrack?: boolean
}

const getUmami = () => {
	if (typeof window === 'undefined' || !window.umami) {
		console.error('umami is not initialized')
		return
	}

	return window.umami as unknown as Umami
}

export const umamiPlugin = (pluginConfig: UmamiPluginConfig) => ({
	config: pluginConfig,
	identify: ({ payload }: { payload: Record<string, any> }) => {
		const umami = getUmami()

		return umami?.identify(payload.userId, payload.traits)
	},
	initialize: ({ config }: { config: UmamiPluginConfig }) => {
		const {
			src,
			websiteId,
			hostUrl,
			autoTrack,
			domains,
			tag,
			excludeSearch,
			excludeHash,
			doNotTrack,
		} = config

		const script = document.createElement('script')

		script.defer = true
		script.src = src

		script.setAttribute('data-website-id', websiteId)

		if (hostUrl) {
			script.setAttribute('data-host-url', hostUrl)
		}

		if (autoTrack !== undefined) {
			script.setAttribute('data-auto-track', autoTrack.toString())
		}

		if (domains?.length) {
			script.setAttribute('data-domains', domains.join(','))
		}

		if (tag) {
			script.setAttribute('data-tag', tag)
		}

		if (excludeSearch !== undefined) {
			script.setAttribute('data-exclude-search', excludeSearch.toString())
		}

		if (excludeHash !== undefined) {
			script.setAttribute('data-exclude-hash', excludeHash.toString())
		}

		if (doNotTrack !== undefined) {
			script.setAttribute('data-do-not-track', doNotTrack.toString())
		}

		document.head.appendChild(script)
	},
	loaded: () => {
		return typeof window !== 'undefined' && !!window.umami
	},
	name: 'umami',
	page: ({ payload, config }: { payload?: any; config: UmamiPluginConfig }) => {
		if (config.autoTrack !== false) return

		const umami = getUmami()
		const { properties } = payload

		return umami?.track((state) => ({
			...state,
			fullUrl: state.url,
			query: properties.query,
			url: properties.route,
		}))
	},
	track: ({
		payload,
	}: {
		payload: {
			event: string
			[key: string]: any
		}
	}) => {
		const umami = getUmami()
		const { event, ...rest } = payload

		return umami?.track(event, rest.properties)
	},
})
